import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Popconfirm,
  Space,
  Pagination,
  Switch,
} from 'antd';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { createStyles } from 'antd-style';
import { theme } from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import type { TableProps, PopconfirmProps, TableColumnsType } from 'antd';
import type { BackDetails } from '../../../../types/BackDetails';
import BackDetailsModal from './BackDetailsModal';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table-container {
      .ant-table-body,
      .ant-table-content {
        scrollbar-width: thin;
        scrollbar-color: #eaeaea transparent;
      }
    }
  `,
}));

const BackDetailsPage: React.FC = () => {
  const [backDetails, setBackDetails] = useState<BackDetails[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<BackDetails | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const { styles } = useStyle();
  const currentModule = useCurrentModule();

  const cancel: PopconfirmProps['onCancel'] = () => {
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchBackDetails = async () => {
    try {
      const data = await apiRequest<BackDetails[]>({
        method: 'get',
        url: 'back-details',
      });
      setBackDetails(data);
    } catch {
      showError('Failed to fetch back details');
    }
  };

  useEffect(() => {
    fetchBackDetails();
  }, []);

  const handleEditClick = (record: BackDetails) => {
    setIsEditMode(true);
    setInitialValues(record);
    setIsModalVisible(true);
  };

  const handleCreate = () => fetchBackDetails();

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (bd_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-back-detail/${bd_id}`,
      });
      showSuccess('Back Detail deleted');
      fetchBackDetails();
    } catch {
      showError('Failed to delete back detail');
    }
  };

  const handleStatusToggle = async (bd_id: number) => {
    setBackDetails((prev) =>
      prev.map((item) =>
        item.bd_id === bd_id
          ? { ...item, bd_status: item.bd_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-back-detail/${bd_id}`,
      });
      showSuccess('Back Detail status updated');
    } catch {
      showError('Failed to update status');
      // Optionally revert UI change on failure
      fetchBackDetails();
    }
  };

  const columns: TableColumnsType<BackDetails> = [
    {
      title: 'Back Detail',
      dataIndex: 'bd_name',
      key: 'bd_name',
      width: 500,
      sorter: (a, b) => a.bd_name.localeCompare(b.bd_name),
      render: (_, record) => <div>{record.bd_name}</div>,
    },
    {
      title: 'Back Mesh Name',
      dataIndex: 'bd_mesh_name',
      key: 'bd_mesh_name',
      width: 500,
      sorter: (a, b) => a.bd_mesh_name.localeCompare(b.bd_mesh_name),
      render: (_, record) => <div>{record.bd_mesh_name}</div>,
    },
    {
      title: 'Image',
      dataIndex: 'bd_image',
      key: 'bd_image',
      width: 500,
      render: (_, record) =>
        record.bd_image ? (
          <img
            src={record.bd_image}
            alt="back detail"
            style={{ width: 40, height: 50, objectFit: 'cover', borderRadius: 8 }}
          />
        ) : (
          '-'
        ),
    },
  ];

  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_, record) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checked={!!record.bd_status}
                onChange={() => handleStatusToggle(record.bd_id)}
              />
              <Button onClick={() => handleEditClick(record)}>
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this back detail?"
              onConfirm={() => handleDelete(record.bd_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  return (
    <>
      <Space
        style={{
          marginBottom: 16,
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Breadcrumb>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Back Details</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Button type="primary" onClick={showCreateModal}>
            Add Back Detail
          </Button>
        )}
      </Space>

      <div style={{ background: colorBgContainer, borderRadius: borderRadiusLG }}>
        <Table<BackDetails>
          tableLayout="auto"
          className={styles.customTable}
          columns={columns}
          dataSource={backDetails.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          rowKey="bd_id"
          pagination={false}
          scroll={{ y: 37 * 13 }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–{' '}
                {Math.min(pagination.current * pagination.pageSize, backDetails.length)} of{' '}
                {backDetails.length}
              </span>
              <Pagination
                {...pagination}
                total={backDetails.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <BackDetailsModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        backDetails={backDetails}
      />
    </>
  );
};

export default BackDetailsPage;
